import React, { useState, ChangeEvent, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { AccountType, useAccount } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";

import { PagePaths } from "@shared/constants";

import AccountService from "@features/createaccount/service/AccountService";
import { RegisterIdentityRequest } from "@features/createaccount/service/AccountRequest";
import { useAuthError } from "@shared/hooks/useAuthError";

import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { cleanFormDataErrors } from "@shared/utils";
import { delay, intervalMiliseconds } from "@shared/utils";
import { getSessionFormValue } from "@shared/utils/storage/sessionFormStorage";

import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_insertUsername } from "@i18n/usualTexts/t_insertUsername";

export interface FormData {
  username: {
    value: string;
    errors: Set<string>;
  };
}

const InsertUsernamePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("signup.insertUsername.pageTitle"));
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { accountData, setAccountData } = useAccount();
  const { currentToken: antiBotToken } = useRecaptchaContext();
  const { handleRegisterError } = useAuthError();
  const [formData, setFormData] = useState<FormData>({
    username: {
      value: accountData.username ?? getSessionFormValue("username") ?? "",
      errors: new Set<string>(),
    },
  });

  function checkThisFormData() {
    const accountService: AccountService = new AccountService(t);
    cleanFormDataErrors(formData);
    accountService.checkUsername(formData.username.value, "username");
    
    // Return true if no errors, false if there are errors
    return !formData.username.errors.size;
  }

  function frontExceptionFunction(errorDescription: Array<string>) {
    formData[errorDescription[0] as keyof FormData].errors.add(
      errorDescription[1]
    );
  }

  async function postFormDatas_and_navigate(
    newAccountData: AccountType
  ) {
    // Get the personal info response from account data
    const personalInfoPostResponse = accountData.personalInfoPostResponse;
    if (!personalInfoPostResponse) {
      throw new Error(t("error.runtime.noPersonalInfoResponse"));
    }

    const intervalMilisecondsValue = intervalMiliseconds(
      personalInfoPostResponse
    );
    await delay(intervalMilisecondsValue);

    const identityPostResponse = await RegisterIdentityRequest(
      formData.username.value,
      personalInfoPostResponse?.access_token,
      antiBotToken || undefined
    );
    console.log(identityPostResponse);

    // Update account data with the response
    const updatedAccountData: AccountType = {
      ...newAccountData,
      identityPostResponse: identityPostResponse,
    };

    setAccountData(updatedAccountData);

    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.CREATEPASSWORD));
  }

  async function handleSubmit() {
    setIsLoading(true);
    setSubmitted(true);

    try {
      const isValid = checkThisFormData();
      
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      const step = 4;
      const newAccountData: AccountType = {
        ...accountData,
        username: formData.username.value,
        step: step,
      };

      await postFormDatas_and_navigate(newAccountData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleRegisterError(error, setServerErrorPopUp, frontExceptionFunction);
      
      setIsLoading(false);
    } finally {
      setFormData({ ...formData });
      await delay(1000);
      setSubmitted(false);
    }
  }

  function handleFormData(e: ChangeEvent<HTMLInputElement>) {
    const { value, name } = e.target;

    formData[name as keyof FormData].value = value;

    setFormData({
      ...formData,
    });
  }

  function back() {
    setAccountData({
      ...accountData,
      step: 2,
    });

    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.BIRTHDAYGENDER));
  }

  return (
    <AuthenticationForm
      title={t(t_insertUsername.title)}
      subtitle={t(t_insertUsername.subtitle)}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      back={back}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <FormField
        autofocus={true}
        name="username"
        type="email"
        required={true}
        onChange={handleFormData}
        value={formData.username.value}
        errors={formData.username.errors}
        submitted={submitted}
      >
        {t(t_insertUsername.emailPlaceholder)}
      </FormField>
    </AuthenticationForm>
  );
};

export default InsertUsernamePage;